import { useEffect, useRef, useState } from 'preact/hooks';
import { Book, Chapter } from '../types';
import {
  extractSentencesFromElement,
  selectSpeechSynthesisVoice,
  createSpeechUtterance,
} from '../utils/ttsUtils';
import { highlightAndScrollToSentenceInElement } from '../utils/domUtils';

interface UseTextToSpeechProps {
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  contentRef: preact.RefObject<HTMLDivElement>;
  chapter: Chapter;
  nextChapter?: Chapter;
  book: Book;
  ttsSettings: {
    ttsRate?: number | string;
    ttsVoice?: string;
  };
}

interface UseTextToSpeechResult {
  currentSentence: string | null;
  highlightCurrentSentence: () => void;
}

export const useTextToSpeech = ({
  isPlaying,
  setIsPlaying,
  contentRef,
  chapter,
  nextChapter,
  book,
  ttsSettings,
}: UseTextToSpeechProps): UseTextToSpeechResult => {
  const [currentSentence, setCurrentSentence] = useState<string | null>(null);
  const [sentences, setSentences] = useState<string[]>([]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const speechSynthesis = window.speechSynthesis;
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const availableVoicesRef = useRef<SpeechSynthesisVoice[]>([]);

  useEffect(() => {
    const handleVoicesChanged = () => {
      availableVoicesRef.current = speechSynthesis.getVoices();
    };
    availableVoicesRef.current = speechSynthesis.getVoices();
    speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
    return () => {
      speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
    };
  }, [speechSynthesis]);

  useEffect(() => {
    if (contentRef.current) {
      const extractedSentences = extractSentencesFromElement(
        contentRef.current,
      );
      setSentences(extractedSentences);
      setCurrentSentenceIndex(0);
      setCurrentSentence(null);
      highlightAndScrollToSentenceInElement(contentRef.current, null);
    }
    speechSynthesis.cancel();
    utteranceRef.current = null;
    setIsPlaying(false);
  }, [chapter.id, contentRef]);

  const highlightCurrentSentence = () => {
    highlightAndScrollToSentenceInElement(contentRef.current, currentSentence);
  };

  useEffect(() => {
    highlightCurrentSentence();
  }, [currentSentence]);

  useEffect(() => {
    const speakCurrentSentence = () => {
      if (currentSentenceIndex >= sentences.length) {
        console.log('Finished chapter, checking for next chapter...');
        if (nextChapter && book) {
          window.location.href = `/f/${book.permalink}/${nextChapter.permalink}`;
        } else {
          setIsPlaying(false);
          setCurrentSentence(null);
        }
        utteranceRef.current = null;
        return;
      }

      if (availableVoicesRef.current.length === 0) {
        console.warn('Voices not loaded, cannot speak yet.');
        setIsPlaying(false);
        return;
      }

      const selectedVoice = selectSpeechSynthesisVoice(
        availableVoicesRef.current,
        ttsSettings.ttsVoice,
      );

      const utterance = createSpeechUtterance(
        sentences[currentSentenceIndex],
        selectedVoice,
        ttsSettings.ttsRate,
      );

      utteranceRef.current = utterance;
      setCurrentSentence(sentences[currentSentenceIndex]);

      utterance.onend = () => {
        utteranceRef.current = null;
        setCurrentSentenceIndex((prevIndex) => prevIndex + 1);
      };

      utterance.onerror = (event) => {
        console.error('SpeechSynthesisUtterance.onerror', event);
        setIsPlaying(false);
        setCurrentSentence(null);
        utteranceRef.current = null;
      };

      speechSynthesis.cancel();
      speechSynthesis.speak(utterance);
    };

    if (isPlaying && sentences.length > 0) {
      if (speechSynthesis.paused && utteranceRef.current) {
        console.log('Resuming speech...');
        speechSynthesis.resume();
      } else if (!speechSynthesis.speaking && !speechSynthesis.pending) {
        console.log('Speaking sentence:', currentSentenceIndex);
        speakCurrentSentence();
      }
    }
  }, [
    isPlaying,
    sentences,
    currentSentenceIndex,
    ttsSettings,
    nextChapter,
    book,
    setIsPlaying,
  ]);

  useEffect(() => {
    if (!isPlaying) {
      if (speechSynthesis.speaking) {
        console.log('Pausing speech...');
        speechSynthesis.pause();
      } else if (!speechSynthesis.speaking && !speechSynthesis.paused) {
        console.log('Cancelling speech due to isPlaying=false...');
        speechSynthesis.cancel();
        utteranceRef.current = null;
      }
    }
  }, [isPlaying, speechSynthesis]);

  useEffect(() => {
    return () => {
      console.log('Chapter changed or unmounting, cancelling speech...');
      speechSynthesis.cancel();
      utteranceRef.current = null;
    };
  }, [chapter.id]);

  return { currentSentence, highlightCurrentSentence };
};
